import { canonicalizeArtifactType } from '@gonfalon/observability-ui/components/Artifact';

export interface FlagArtifactIdentity {
  projectKey: string;
  flagKey: string;
}

export interface AlertArtifactIdentity {
  alertId: string;
}

/**
 * Flag artifacts store identity as `{projectKey}/{flagKey}`. Flag keys may contain
 * `/`, so only the first slash splits project from flag.
 */
export function parseFlagArtifactIdentity(externalId: string): FlagArtifactIdentity | null {
  const trimmed = externalId.trim();
  if (!trimmed) {
    return null;
  }
  const slash = trimmed.indexOf('/');
  if (slash <= 0 || slash >= trimmed.length - 1) {
    return null;
  }
  const projectKey = trimmed.slice(0, slash).trim();
  const flagKey = trimmed.slice(slash + 1).trim();
  if (!projectKey || !flagKey) {
    return null;
  }
  return { projectKey, flagKey };
}

/** Alert artifacts store the Observability alert ID as `external_id`. */
export function parseAlertArtifactIdentity(externalId: string): AlertArtifactIdentity | null {
  const alertId = externalId.trim();
  return alertId ? { alertId } : null;
}

export function isPullRequestArtifactType(type: string): boolean {
  return canonicalizeArtifactType(type) === 'pull_request';
}

export function isFlagArtifactType(type: string): boolean {
  return canonicalizeArtifactType(type) === 'flag';
}

export function isAlertArtifactType(type: string): boolean {
  return canonicalizeArtifactType(type) === 'log_alert';
}

/**
 * Prefer merged/draft over raw GitHub `state` — merged PRs are `state=closed`,
 * and draft PRs are `state=open`, so those flags are the useful badge labels.
 */
export function derivePullRequestStateLabel(pr: { merged: boolean; draft: boolean; state: string }): string {
  if (pr.merged) {
    return 'merged';
  }
  if (pr.draft) {
    return 'draft';
  }
  return pr.state;
}

export interface PullRequestReviewLike {
  user: string;
  state: string;
  submitted_at?: string | number | null;
}

function submittedAtMs(raw: string | number | null | undefined): number {
  if (raw == null || raw === '') {
    return Number.NEGATIVE_INFINITY;
  }
  const ms = typeof raw === 'number' ? raw : Date.parse(String(raw));
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Count current review dispositions from GitHub review history. GitHub keeps every
 * review event, so a reviewer who requested changes and later approved would
 * otherwise inflate both counters — keep only each reviewer's latest review.
 */
export function countLatestPullRequestReviews(reviews: readonly PullRequestReviewLike[]): {
  approvals: number;
  changesRequested: number;
} {
  const latestByUser = new Map<string, PullRequestReviewLike>();
  for (const review of reviews) {
    const existing = latestByUser.get(review.user);
    if (!existing || submittedAtMs(review.submitted_at) >= submittedAtMs(existing.submitted_at)) {
      latestByUser.set(review.user, review);
    }
  }

  let approvals = 0;
  let changesRequested = 0;
  for (const review of latestByUser.values()) {
    const state = review.state.toUpperCase();
    if (state === 'APPROVED') {
      approvals += 1;
    } else if (state === 'CHANGES_REQUESTED') {
      changesRequested += 1;
    }
  }
  return { approvals, changesRequested };
}
